import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, CheckCircle } from 'lucide-react';

interface ResumeMatchAnalysisEmeraldProps {
  beforeScore: number;
  afterScore: number;
  improvement: number;
  jobTitle: string;
  companyName: string;
  onShowAnalysisModal: () => void;
}

export const ResumeMatchAnalysisEmerald: React.FC<ResumeMatchAnalysisEmeraldProps> = ({
  beforeScore,
  afterScore,
  improvement,
  jobTitle,
  companyName,
  onShowAnalysisModal
}) => {
  const navigate = useNavigate();

  return (
    <div className="panel-emerald">
      {/* Panel Header */}
      <div className="flex items-center gap-3 mb-7">
        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center text-white text-xl shadow-md">
          📊
        </div>
        <h2 className="font-outfit text-[32px] font-bold text-slate-900">Resume Match Analysis</h2>
      </div>

      {/* Job Info Section */}
      <div className="bg-emerald-100 border-2 border-emerald-500/15 rounded-2xl p-6 mb-7 grid grid-cols-[1fr_auto] gap-5 items-center shadow-sm">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Position</span>
            <span className="font-outfit text-lg font-semibold text-slate-900">{jobTitle}</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Company</span>
            <span className="font-outfit text-lg font-semibold text-slate-900">{companyName}</span>
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white py-3 px-6 rounded-xl text-sm font-semibold flex items-center gap-2 shadow-lg border-2 border-white/20">
          ✓ Successfully Optimized!
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-4 mb-7">
        {/* Before Card */}
        <div className="stat-card-before relative bg-white border-2 border-red-500 rounded-2xl p-7 flex flex-col items-center gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl shadow-md overflow-visible">
          <div className="stat-header text-base font-bold uppercase tracking-wider text-center text-red-600 relative">
            <span className="stat-label absolute -top-[42px] left-1/2 -translate-x-1/2 bg-red-600 text-white py-2 px-5 rounded-lg text-xs font-extrabold tracking-wider shadow-lg z-10">
              BEFORE
            </span>
            Original Resume
          </div>
          <div className="relative w-[140px] h-[140px]">
            <div 
              className="w-full h-full rounded-full absolute"
              style={{
                background: `conic-gradient(#dc2626 ${beforeScore}%, #e5e7eb ${beforeScore}%)`
              }}
            />
            <div className="absolute top-3 left-3 right-3 bottom-3 bg-white rounded-full flex flex-col items-center justify-center">
              <span className="font-outfit text-4xl font-extrabold text-slate-900">{beforeScore}%</span>
              <span className="text-[11px] text-slate-900 mt-0.5 font-semibold">Match Rate</span>
            </div>
          </div>
          <div className="bg-red-600/10 text-red-600 border-2 border-red-600/20 py-2.5 px-4 rounded-xl text-sm font-bold text-center">
            {beforeScore >= 70 ? "Good baseline" : beforeScore >= 50 ? "Needs Improvement" : "Requires Optimization"}
          </div>
        </div>

        {/* After Card */}
        <div className="stat-card-after relative bg-white border-2 border-emerald-600 rounded-2xl p-7 flex flex-col items-center gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl shadow-md overflow-visible">
          <div className="stat-header text-base font-bold uppercase tracking-wider text-center text-emerald-600 relative">
            <span className="stat-label absolute -top-[42px] left-1/2 -translate-x-1/2 bg-emerald-600 text-white py-2 px-5 rounded-lg text-xs font-extrabold tracking-wider shadow-lg z-10">
              AFTER
            </span>
            Tailored Resume
          </div>
          <div className="relative w-[140px] h-[140px]">
            <div 
              className="w-full h-full rounded-full absolute"
              style={{
                background: `conic-gradient(#059669 ${afterScore}%, #e5e7eb ${afterScore}%)`
              }}
            />
            <div className="absolute top-3 left-3 right-3 bottom-3 bg-white rounded-full flex flex-col items-center justify-center">
              <span className="font-outfit text-4xl font-extrabold text-slate-900">{afterScore}%</span>
              <span className="text-[11px] text-slate-900 mt-0.5 font-semibold">Match Rate</span>
            </div>
          </div>
          <div className="bg-emerald-600/10 text-emerald-600 border-2 border-emerald-600/20 py-2.5 px-4 rounded-xl text-sm font-bold text-center">
            {afterScore >= 85 ? "Excellent Match!" : afterScore >= 70 ? "Strong Candidate" : "Good Improvement"}
          </div>
        </div>

        {/* Boost Card */}
        <div className="stat-card-boost bg-white border-2 border-violet-500 rounded-2xl p-7 flex flex-col items-center gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl shadow-md">
          <div className="text-base font-bold uppercase tracking-wider text-center text-violet-600">
            Performance Boost
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl p-6 text-white flex flex-col items-center gap-3 border-2 border-white/20 shadow-lg w-full">
            <span className="font-outfit text-5xl font-extrabold">+{improvement}</span>
            <span className="text-[13px] font-semibold opacity-90">Points Gained</span>
            <div className="w-full h-2 bg-white/20 rounded overflow-hidden">
              <div 
                className="h-full bg-white rounded"
                style={{ width: `${afterScore}%` }}
              />
            </div>
            <span className="text-xs opacity-90">Progress: {afterScore}%</span>
          </div>
        </div>
      </div>

      {/* Achievement Banner */}
      <div className="bg-blue-100 border-2 border-blue-500/15 rounded-2xl p-7 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center text-white text-lg">
            🎯
          </div>
          <h3 className="font-outfit text-[22px] font-bold text-slate-900">Outstanding Optimization Achieved!</h3>
        </div>
        <p className="text-slate-500 leading-relaxed mb-4 text-[15px]">
          Your resume has been strategically enhanced, increasing your match compatibility from{' '}
          <span className="font-semibold text-slate-900">{beforeScore}% to {afterScore}%</span> - a remarkable improvement of{' '}
          <span className="font-semibold text-slate-900">+{improvement} points!</span> This optimization significantly strengthens your candidacy for the {jobTitle} position at {companyName}.
        </p>
        <div className="flex gap-3 flex-wrap">
          <div className="bg-green-500/10 text-green-600 border-2 border-green-500/20 py-2.5 px-4 rounded-xl text-[13px] font-semibold flex items-center gap-1.5">
            ✓ ATS Optimized
          </div>
          <div className="bg-emerald-600/10 text-emerald-600 border-2 border-emerald-600/20 py-2.5 px-4 rounded-xl text-[13px] font-semibold flex items-center gap-1.5">
            📈 Keyword Enhanced
          </div>
          <div className="bg-violet-600/10 text-violet-600 border-2 border-violet-600/20 py-2.5 px-4 rounded-xl text-[13px] font-semibold flex items-center gap-1.5">
            ⚡ Performance Boosted
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-4 mt-7">
        <button 
          onClick={onShowAnalysisModal}
          className="py-4 px-6 rounded-xl font-outfit text-base font-semibold border-2 border-transparent cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-300 bg-blue-800 text-white shadow-lg hover:bg-blue-900 hover:-translate-y-0.5 hover:shadow-xl"
        >
          <BarChart3 className="w-5 h-5" />
          Analysis Report
        </button>
        <button 
          onClick={() => navigate('/?view=resume-library', { state: { autoSelectTailored: true } })}
          className="py-4 px-6 rounded-xl font-outfit text-base font-semibold border-2 border-transparent cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-300 bg-emerald-600 text-white shadow-lg hover:bg-emerald-700 hover:-translate-y-0.5 hover:shadow-xl"
        >
          <CheckCircle className="w-5 h-5" />
          Resume Library
        </button>
      </div>

      <style>{`
        .panel-emerald {
          --bg-primary: #ffffff;
          --bg-secondary: #f8fffe;
          --bg-card: #ffffff;
          --accent-primary: #059669;
          --accent-secondary: #10b981;
          --text-primary: #0f172a;
          --text-secondary: #64748b;
          --success: #22c55e;
          --warning: #f59e0b;
          --border: #cbd5e0;
          
          background: var(--bg-primary);
          border-radius: 24px;
          padding: 40px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.1);
          border: 1px solid var(--border);
        }

        .font-outfit {
          font-family: 'Outfit', 'DM Sans', sans-serif;
        }

        @media (max-width: 768px) {
          .panel-emerald {
            padding: 28px;
          }
          
          .panel-emerald .grid-cols-3 {
            grid-template-columns: 1fr;
          }
          
          .panel-emerald .grid-cols-2 {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};